import { useEffect, useState } from "react";
import {
  fetchAdminContacts,
  createAdminContact,
  updateAdminContact,
  deleteAdminContact,
} from "@/api/auth";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState("all"); // all, active, inactive

  const [form, setForm] = useState({
    title: "",
    url: "",
    icon: "",
    platform: "other",
    is_active: true,
  });

  // ================= Load =================
  const loadContacts = async () => {
    try {
      const res = await fetchAdminContacts();

      if (Array.isArray(res)) {
        setContacts(res);
      } else if (Array.isArray(res?.contacts)) {
        setContacts(res.contacts);
      } else {
        setContacts([]);
      }
    } catch (e) {
      console.error(e);
      setContacts([]);
    }
  };

  useEffect(() => {
    loadContacts();
  }, []);

  // ================= Filter & Search =================
  const filteredContacts = contacts.filter(contact => {
    // Filter by active status
    if (filterActive === "active" && !contact.is_active) return false;
    if (filterActive === "inactive" && contact.is_active) return false;
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (contact.title || "").toLowerCase().includes(term) ||
        (contact.url || "").toLowerCase().includes(term) ||
        (contact.icon || "").toLowerCase().includes(term) ||
        (contact.platform || "").toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  // ================= Submit =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const contactData = {
        ...form,
        url: form.url.trim(),
        title: form.title.trim(),
        icon: form.icon.trim(),
      };

      if (editingId) {
        await updateAdminContact(editingId, contactData);
      } else {
        await createAdminContact(contactData);
      }

      resetForm();
      loadContacts();
    } catch (e) {
      console.error(e);
      setError("حدث خطأ أثناء الحفظ");
    } finally {
      setLoading(false);
    }
  };

  // ================= Delete =================
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف رابط التواصل هذا؟")) return;
    
    try {
      await deleteAdminContact(id);
      loadContacts();
    } catch (e) {
      console.error(e);
      setError("فشل حذف رابط التواصل");
    }
  };

  // ================= Edit =================
  const handleEdit = (contact) => {
    setEditingId(contact.id);
    setForm({
      title: contact.title || "",
      url: contact.url || "",
      icon: contact.icon || "",
      platform: contact.platform || "other",
      is_active: Boolean(contact.is_active),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: "",
      url: "",
      icon: "",
      platform: "other",
      is_active: true,
    });
  };

  // ================= Platform Icons =================
  const platformIcons = {
    whatsapp: "💬",
    telegram: "📱",
    instagram: "📸",
    twitter: "🐦",
    facebook: "📘",
    linkedin: "💼",
    email: "📧",
    phone: "📞",
    location: "📍",
    website: "🌐",
    other: "🔗"
  };

  const platformOptions = [
    { value: "whatsapp", label: "واتساب", icon: "💬" },
    { value: "telegram", label: "تلجرام", icon: "📱" },
    { value: "instagram", label: "إنستجرام", icon: "📸" },
    { value: "twitter", label: "تويتر", icon: "🐦" },
    { value: "facebook", label: "فيسبوك", icon: "📘" },
    { value: "linkedin", label: "لينكد إن", icon: "💼" },
    { value: "email", label: "البريد الإلكتروني", icon: "📧" },
    { value: "phone", label: "الهاتف", icon: "📞" },
    { value: "location", label: "الموقع", icon: "📍" },
    { value: "website", label: "الموقع الإلكتروني", icon: "🌐" },
    { value: "other", label: "أخرى", icon: "🔗" }
  ];

  // ================= UI =================
  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <div className="header-content">
          <h1 className="contacts-title">إدارة روابط التواصل</h1>
          <p className="contacts-subtitle">أضف وعُدّل روابط التواصل الاجتماعية والاتصالية</p>
        </div>
        <div className="header-stats">
          <div className="stat">
            <span className="stat-value">{contacts.length}</span>
            <span className="stat-label">إجمالي الروابط</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {contacts.filter(c => c.is_active).length}
            </span>
            <span className="stat-label">نشطة</span>
          </div>
        </div>
      </div>

      {/* ===== FORM ===== */}
      <div className="contacts-form-container">
        <h2 className="form-title">
          {editingId ? "تعديل رابط التواصل" : "إضافة رابط جديد"}
        </h2>
        
        <form onSubmit={handleSubmit} className="contacts-form">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                العنوان *
                <span className="label-hint">مثال: واتساب، تويتر، البريد الإلكتروني</span>
              </label>
              <input
                className="form-input"
                placeholder="أدخل عنوان رابط التواصل"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                الرابط *
                <span className="label-hint">رابط التواصل الكامل</span>
              </label>
              <input
                className="form-input"
                placeholder="https://example.com"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                required
                type="url"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                المنصة
                <span className="label-hint">اختر نوع المنصة</span>
              </label>
              <div className="platform-selector">
                {platformOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`platform-option ${form.platform === option.value ? "selected" : ""}`}
                    onClick={() => setForm({ ...form, platform: option.value, icon: option.value })}
                  >
                    <span className="platform-icon">{option.icon}</span>
                    <span className="platform-label">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                اسم الأيقونة
                <span className="label-hint">اسم الأيقونة (اختياري)</span>
              </label>
              <input
                className="form-input"
                placeholder="whatsapp, telegram, phone..."
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
              />
              <div className="icon-preview">
                <span className="preview-label">معاينة:</span>
                <span className="preview-icon">
                  {platformIcons[form.platform] || platformIcons.other}
                </span>
                <span className="preview-text">{form.icon || form.platform}</span>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <span className="checkbox-text">تفعيل الرابط</span>
                <span className="checkbox-hint">عرض هذا الرابط في الموقع</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  جاري الحفظ...
                </>
              ) : editingId ? "تحديث الرابط" : "إضافة الرابط"}
            </button>
            
            {editingId && (
              <button 
                type="button"
                className="cancel-btn"
                onClick={resetForm}
              >
                إلغاء التعديل
              </button>
            )}
          </div>

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </form>
      </div>

      {/* ===== CONTROLS ===== */}
      <div className="controls-container">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="ابحث عن روابط التواصل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm("")}
              aria-label="مسح البحث"
            >
              ×
            </button>
          )}
        </div>

        <div className="filter-controls">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${filterActive === "all" ? "active" : ""}`}
              onClick={() => setFilterActive("all")}
            >
              الكل ({contacts.length})
            </button>
            <button 
              className={`filter-tab ${filterActive === "active" ? "active" : ""}`}
              onClick={() => setFilterActive("active")}
            >
              النشطة ({contacts.filter(c => c.is_active).length})
            </button>
            <button 
              className={`filter-tab ${filterActive === "inactive" ? "active" : ""}`}
              onClick={() => setFilterActive("inactive")}
            >
              المخفية ({contacts.filter(c => !c.is_active).length})
            </button>
          </div>
        </div>
      </div>

      {/* ===== LIST ===== */}
      <div className="contacts-list-container">
        {filteredContacts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔗</div>
            <h3 className="empty-title">
              {searchTerm || filterActive !== "all" 
                ? "لا توجد نتائج" 
                : "لا توجد روابط تواصل"
              }
            </h3>
            <p className="empty-subtitle">
              {searchTerm 
                ? "جرب مصطلحات بحث مختلفة" 
                : filterActive !== "all"
                ? `لا توجد روابط ${filterActive === "active" ? "نشطة" : "مخفية"}`
                : "ابدأ بإضافة أول رابط تواصل"
              }
            </p>
            {(searchTerm || filterActive !== "all") && (
              <button 
                className="reset-filters-btn"
                onClick={() => {
                  setSearchTerm("");
                  setFilterActive("all");
                }}
              >
                إعادة تعيين الفلاتر
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="list-header">
              <h3 className="list-title">
                روابط التواصل ({filteredContacts.length})
                {searchTerm && (
                  <span className="search-term">: "{searchTerm}"</span>
                )}
              </h3>
            </div>

            <div className="contacts-grid">
              {filteredContacts.map((contact) => (
                <div 
                  key={contact.id} 
                  className={`contact-card ${!contact.is_active ? "inactive" : ""} ${editingId === contact.id ? "editing" : ""}`}
                >
                  <div className="card-header">
                    <div className="contact-icon">
                      {platformIcons[contact.platform] || platformIcons[contact.icon] || platformIcons.other}
                    </div>
                    <div className="contact-info">
                      <h3 className="contact-title">{contact.title}</h3>
                      <span className="contact-platform">
                        {platformOptions.find(p => p.value === contact.platform)?.label || "أخرى"}
                      </span>
                    </div>
                    <span className={`status-badge ${contact.is_active ? "active" : "inactive"}`}>
                      {contact.is_active ? "نشط" : "مخفي"}
                    </span>
                  </div>

                  <div className="card-content">
                    <a 
                      href={contact.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="contact-link"
                    >
                      <span className="link-text">{contact.url}</span>
                      <span className="link-icon">↗</span>
                    </a>
                    
                    {contact.icon && (
                      <div className="contact-meta">
                        <span className="meta-item">
                          <span className="meta-label">الأيقونة:</span>
                          <code className="meta-value">{contact.icon}</code>
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="card-footer">
                    <div className="footer-actions">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(contact)}
                      >
                        <span className="action-icon">✏️</span>
                        تعديل
                      </button>
                      <button 
                        className="action-btn test-btn"
                        onClick={() => window.open(contact.url, '_blank')}
                      >
                        <span className="action-icon">🔗</span>
                        تجربة
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(contact.id)}
                      >
                        <span className="action-icon">🗑️</span>
                        حذف
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        .contacts-container {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .contacts-header {
          background: linear-gradient(135deg, #ec4899, #d946ef);
          border-radius: 16px;
          padding: 2rem;
          color: white;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 2rem;
        }

        .header-content {
          flex: 1;
        }

        .contacts-title {
          font-size: 2.2rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .contacts-subtitle {
          font-size: 1.1rem;
          opacity: 0.9;
          margin: 0;
        }

        .header-stats {
          display: flex;
          gap: 1.5rem;
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          min-width: 100px;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.875rem;
          opacity: 0.8;
        }

        .contacts-form-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .form-title {
          color: #1e293b;
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #ec4899;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .form-group:nth-child(3),
        .form-group:nth-child(5) {
          grid-column: 1 / -1;
        }

        .form-label {
          color: #334155;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .label-hint {
          color: #64748b;
          font-weight: normal;
          font-size: 0.875rem;
        }

        .form-input {
          padding: 0.875rem 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
          width: 100%;
        }

        .form-input:focus {
          outline: none;
          border-color: #ec4899;
          background: white;
          box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
        }

        .platform-selector {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 0.5rem;
          max-height: 200px;
          overflow-y: auto;
          padding: 0.5rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
        }

        .platform-option {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          padding: 0.75rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
        }

        .platform-option:hover {
          border-color: #cbd5e1;
          background: #f1f5f9;
        }

        .platform-option.selected {
          border-color: #ec4899;
          background: #fdf2f8;
          color: #ec4899;
        }

        .platform-icon {
          font-size: 1.5rem;
        }

        .platform-label {
          font-size: 0.75rem;
          font-weight: 500;
        }

        .icon-preview {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 1rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .preview-label {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .preview-icon {
          font-size: 1.5rem;
          background: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #e2e8f0;
        }

        .preview-text {
          font-family: monospace;
          font-size: 0.875rem;
          color: #475569;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .checkbox-group {
          padding: 1rem;
          background: #f1f5f9;
          border-radius: 10px;
        }

        .checkbox-label {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          cursor: pointer;
        }

        .checkbox-input {
          width: 18px;
          height: 18px;
          accent-color: #ec4899;
          margin-left: 0.5rem;
        }

        .checkbox-text {
          font-weight: 600;
          color: #334155;
        }

        .checkbox-hint {
          color: #64748b;
          font-size: 0.875rem;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .submit-btn {
          background: linear-gradient(135deg, #ec4899, #d946ef);
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 160px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(236, 72, 153, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cancel-btn {
          background: #f1f5f9;
          color: #64748b;
          border: 2px solid #cbd5e1;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-alert {
          background: #fef2f2;
          color: #dc2626;
          padding: 1rem;
          border-radius: 10px;
          border: 1px solid #fecaca;
          margin-top: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .error-icon {
          font-size: 1.2rem;
        }

        .controls-container {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          border: 1px solid #e2e8f0;
        }

        .search-box {
          flex: 1;
          max-width: 400px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 3rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
        }

        .search-input:focus {
          outline: none;
          border-color: #ec4899;
          background: white;
          box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
        }

        .search-icon {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
        }

        .clear-search {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          font-size: 1.2rem;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }

        .clear-search:hover {
          background: #e2e8f0;
        }

        .filter-tabs {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .filter-tab {
          padding: 0.75rem 1.5rem;
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          color: #64748b;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .filter-tab:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .filter-tab.active {
          background: #ec4899;
          color: white;
          border-color: #ec4899;
        }

        .contacts-list-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.3;
        }

        .empty-title {
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .empty-subtitle {
          color: #94a3b8;
          margin-bottom: 1.5rem;
        }

        .reset-filters-btn {
          background: #f1f5f9;
          color: #475569;
          border: 2px solid #cbd5e1;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .reset-filters-btn:hover {
          background: #e2e8f0;
        }

        .list-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .list-title {
          color: #1e293b;
          font-size: 1.25rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .search-term {
          color: #ec4899;
          font-weight: 500;
          font-size: 1rem;
        }

        .contacts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .contact-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .contact-card:hover {
          border-color: #cbd5e1;
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .contact-card.inactive {
          opacity: 0.6;
          background: #f8fafc;
        }

        .contact-card.editing {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .card-header {
          display: flex;
          align-items: center;
          padding: 1.25rem;
          background: linear-gradient(135deg, #fdf2f8, #fce7f3);
          gap: 1rem;
        }

        .contact-icon {
          width: 50px;
          height: 50px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          border: 2px solid #e2e8f0;
          flex-shrink: 0;
        }

        .contact-info {
          flex: 1;
        }

        .contact-title {
          color: #1e293b;
          font-size: 1.125rem;
          font-weight: 700;
          margin: 0 0 0.25rem 0;
        }

        .contact-platform {
          color: #64748b;
          font-size: 0.875rem;
          background: #f1f5f9;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          display: inline-block;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .status-badge.active {
          background: #d1fae5;
          color: #059669;
        }

        .status-badge.inactive {
          background: #fecaca;
          color: #dc2626;
        }

        .card-content {
          padding: 1.25rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .contact-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
          text-decoration: none;
          color: #3b82f6;
          font-weight: 500;
          transition: all 0.3s ease;
          margin-bottom: 1rem;
        }

        .contact-link:hover {
          background: #e0f2fe;
          color: #1d4ed8;
        }

        .link-text {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .link-icon {
          font-size: 1rem;
          margin-right: 0.5rem;
        }

        .contact-meta {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .meta-label {
          color: #64748b;
          font-size: 0.875rem;
        }

        .meta-value {
          font-family: monospace;
          font-size: 0.875rem;
          background: #f1f5f9;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          color: #475569;
        }

        .card-footer {
          padding: 1rem 1.25rem;
        }

        .footer-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        .action-btn {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          border: none;
        }

        .edit-btn {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .edit-btn:hover {
          background: #bfdbfe;
        }

        .test-btn {
          background: #d1fae5;
          color: #059669;
        }

        .test-btn:hover {
          background: #a7f3d0;
        }

        .delete-btn {
          background: #fee2e2;
          color: #dc2626;
        }

        .delete-btn:hover {
          background: #fecaca;
        }

        .action-icon {
          font-size: 1rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .contacts-container {
            padding: 1rem;
          }

          .contacts-header {
            flex-direction: column;
            text-align: center;
            padding: 1.5rem;
          }

          .header-stats {
            width: 100%;
            justify-content: center;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .controls-container {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            max-width: 100%;
          }

          .filter-tabs {
            justify-content: center;
          }

          .contacts-grid {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .submit-btn, .cancel-btn {
            width: 100%;
            justify-content: center;
          }

          .platform-selector {
            grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          }
        }

        @media (max-width: 480px) {
          .contacts-title {
            font-size: 1.75rem;
          }

          .contacts-form-container,
          .contacts-list-container {
            padding: 1.5rem;
          }

          .stat {
            min-width: 80px;
            padding: 0.75rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .card-header {
            flex-wrap: wrap;
          }

          .footer-actions {
            flex-wrap: wrap;
          }

          .action-btn {
            flex: 1;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}